
import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Portfolio, PortfolioType } from '@/types/portfolio';
import { toast } from 'sonner';

export const usePortfolios = () => {
  const queryClient = useQueryClient();
  
  const {
    data: portfolios,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['portfolios'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('portfolios')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data as Portfolio[];
    }
  });

  const createPortfolio = useMutation({
    mutationFn: async (portfolio: Omit<Portfolio, 'id' | 'created_at' | 'updated_at' | 'user_id'>) => {
      // Get the current user's ID
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) throw new Error('You must be logged in to create a portfolio');
      
      const { data, error } = await supabase
        .from('portfolios')
        .insert({
          ...portfolio,
          user_id: user.id
        })
        .select()
        .single();
      
      if (error) throw error;
      return data as Portfolio;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portfolios'] });
      toast.success('Portfolio created successfully');
    },
    onError: (error: Error) => {
      toast.error(`Error creating portfolio: ${error.message}`);
    }
  });

  const deletePortfolio = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('portfolios')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      return id;
    },
    onSuccess: (id) => {
      queryClient.invalidateQueries({ queryKey: ['portfolios'] });
      toast.success('Portfolio deleted successfully');
    },
    onError: (error: Error) => {
      toast.error(`Error deleting portfolio: ${error.message}`);
    }
  });

  return {
    portfolios,
    isLoading,
    error,
    refetch,
    createPortfolio,
    deletePortfolio
  };
};
